/**
 * Privacy Policy page — analytics-focused, plain-language disclosure.
 *
 * This is NOT legal advice. It exists so visitors can understand
 * exactly what analytics data CollectorUp collects and how to opt
 * out. Content should be reviewed by a lawyer before any regulated
 * launch.
 */
import React from "react";
import { AnalyticsPreferencesLink } from "@/components/AnalyticsConsentBanner";

export default function PrivacyPolicy() {
  const lastUpdated = "August 2026";
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8" data-testid="privacy-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
          Privacy
        </div>
        <h1 className="font-serif text-3xl md:text-4xl mt-1">Privacy Policy</h1>
        <p className="text-xs text-[var(--smoke)] mt-2">
          Last updated {lastUpdated}. This document explains, in plain
          language, how CollectorUp uses website analytics. It is
          informational and is not legal advice.
        </p>
      </div>

      <section className="space-y-3 mb-8">
        <h2 className="font-bold text-lg">What we do measure</h2>
        <p className="text-sm">
          To understand which parts of CollectorUp work well and where
          people get stuck, we use two industry-standard analytics
          services when you consent to them:
        </p>
        <ul className="list-disc pl-5 text-sm space-y-2">
          <li>
            <strong>Google Analytics 4.</strong> Records the pages you
            visit, the buttons you click, how you arrived at the site,
            your approximate location (city / country level, derived
            from your IP address which Google immediately anonymises),
            your device type, browser, screen size, and the general
            source of your visit (direct, search engine, social link,
            etc.).
          </li>
          <li>
            <strong>Microsoft Clarity.</strong> Records anonymous
            session recordings and generates aggregated heatmaps of
            where visitors click and scroll. All text inputs (name,
            email, phone, addresses, payment fields, order notes,
            admin notes, claim codes) are automatically masked before
            recording, so we never see what you typed into those
            fields.
          </li>
        </ul>
      </section>

      <section className="space-y-3 mb-8">
        <h2 className="font-bold text-lg">What we do not send to analytics</h2>
        <p className="text-sm">
          CollectorUp never intentionally transmits the following to
          Google Analytics or Microsoft Clarity:
        </p>
        <ul className="list-disc pl-5 text-sm space-y-1">
          <li>Your name</li>
          <li>Your email address</li>
          <li>Your phone number</li>
          <li>Your shipping or billing address</li>
          <li>Zelle or other payment references</li>
          <li>Your order number, quote ID, or magic-link token</li>
          <li>Certificate claim codes or certificate numbers</li>
          <li>Notes attached to an order, quote, or certificate</li>
          <li>Anything from private admin routes that would identify a customer</li>
        </ul>
        <p className="text-sm">
          Dynamic URLs that would otherwise contain these identifiers
          (for example the order tracker link that lands you here from
          your email) are normalised before they are handed to
          analytics — for example, <code>/orders/GM-26-ABC12</code>
          becomes <code>/orders/order-tracker</code>.
        </p>
      </section>

      <section className="space-y-3 mb-8">
        <h2 className="font-bold text-lg">How you can control analytics</h2>
        <p className="text-sm">
          The first time you visit CollectorUp you&apos;ll see a small
          banner at the bottom of the page asking whether you&apos;re
          happy for us to collect analytics. You can:
        </p>
        <ul className="list-disc pl-5 text-sm space-y-1">
          <li>
            <strong>Accept Analytics</strong> — enables Google Analytics 4
            and Microsoft Clarity for the current browser.
          </li>
          <li>
            <strong>Reject Non-Essential</strong> — keeps both services
            switched off for this browser. Essential website functions
            (checkout, order tracking, sign-in, etc.) continue to work
            normally.
          </li>
        </ul>
        <p className="text-sm">
          You can change your choice at any time using the{" "}
          <AnalyticsPreferencesLink className="text-[var(--panini-red)]" />
          {" "}link (also available in the site footer). Rejecting
          analytics anywhere on the site is just as easy — and just
          as prominent — as accepting it.
        </p>
      </section>

      <section className="space-y-3 mb-8">
        <h2 className="font-bold text-lg">Data retention</h2>
        <p className="text-sm">
          Google Analytics retains event data for the retention window
          configured in the CollectorUp property (default 2 months for
          user-level data, up to 14 months for aggregate reports).
          Microsoft Clarity session recordings are stored by Microsoft
          for their standard retention window (currently a rolling 30
          days) after which the recording is discarded. Neither
          provider is used for advertising retargeting on this site.
        </p>
      </section>

      <section className="space-y-3 mb-8">
        <h2 className="font-bold text-lg">Contact</h2>
        <p className="text-sm">
          Questions about analytics or privacy? Email{" "}
          <a href="mailto:privacy@collectorup.com"
             className="underline font-semibold"
             data-testid="privacy-contact-email">
            privacy@collectorup.com
          </a>{" "}
          and Gil will get back to you personally.
        </p>
      </section>

      <div className="text-xs text-[var(--smoke)] mt-8">
        This document is informational. It is not legal advice. If
        you&apos;re publishing your own site, please consult a lawyer.
      </div>
    </div>
  );
}
